import { useTranslation } from 'next-i18next';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { Modal } from '@components/common';
import { resetBulkApplyFeed } from '@store/reducers/jobReducer';
import { useAppDispatch } from '@store/index';
import { BulkApplyJobList } from './BulkApplyJobList';

// Interface
// ******************************************
export interface BulkApplyModalProps {
  jobList: ApiSchema<'JobItem'>[];
  locale: Locale;
}


/**
 * @description Bulk apply modal component.
 *
 * - **jobList** - ApiSchema<'JobItem'>[] - A list with similar jobs for bulk apply.
 * - **locale** - Locale - The current app locale.
 */
export const BulkApplyModal = NiceModal.create((props: BulkApplyModalProps) => {
  // Destructure props
  const { jobList, locale } = props;

  // Translation
  const { t } = useTranslation('common');

  // Use a hook to manage the modal state
  const modal = useModal();

  // Redux dispatch
  const appDispatch = useAppDispatch();

  // Close modal
  const closeModal = () => {
    // reset the bulk apply feed when closing the modal
    appDispatch(resetBulkApplyFeed());
    void modal.hide();
  };

  // Render component
  // *****************************************************
  return (
    <Modal
      isOpen={modal.visible}
      onClose={
        /* Pass an empty function because we don't want to close the modal on backdrop click or on ESC key. */
        () => {}
      }
      size="xl"
    >
      <Modal.Header content={t('bulk.apply.text.heading')} onClose={closeModal} />
      <Modal.Body className="bg-surface-100">
        {jobList && (
          <div className="container">
            <BulkApplyJobList jobList={jobList} locale={locale} onBulkApplySuccess={closeModal} />
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
});

// typed helper for consumers
export const showBulkApplyModal = (args: BulkApplyModalProps) => NiceModal.show(BulkApplyModal, args);
